import { useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Users, CreditCard, X, ShieldCheck, SlidersHorizontal, Bell, Mail, Clock } from "lucide-react";
import { DEMO_THERAPIST } from "../demo/mock-data";
import { SampleConsentDoc } from "../demo/SampleConsentDoc";
import { cn } from "@/lib/utils";

/**
 * DemoSettings (/settings) — the single ACCOUNT HUB, mirroring the real Psy180 app
 * where Profile lives under Settings (the standalone Profile tab was removed).
 * Three sections, top-down: Profile (clinic identity + roster + billing) → Account
 * & notifications (timezone + reminder channels + lead times) → Agreements. All
 * static/inert per the isolation guard: imports only vendored @/..., lucide, cn,
 * and in-memory fixtures — never lib/auth, lib/db, lib/billing, or @stripe/*.
 */

const ROSTER = [
  { name: DEMO_THERAPIST.name, role: "Owner · Clinician", initials: "DS", status: "active" as const },
  { name: "Alex Placeholder, LMFT", role: "Clinician", initials: "AP", status: "active" as const },
  { name: "Sam Example, LPC", role: "Clinician", initials: "SE", status: "invited" as const },
];

const NOTIFICATION_PREFS = [
  { id: "email", label: "Email reminders", hint: "Reminders to you, and to your client or their guardian.", on: true },
  { id: "in-app", label: "In-app upcoming panel", hint: "Upcoming sessions and reminders on your dashboard.", on: true },
];

/** Reminder lead-time presets in minutes (matches the real Account & Notifications form). */
const LEAD_PRESETS = [15, 30, 60, 120, 1440, 2880];
function formatMinutes(m: number): string {
  if (m % 1440 === 0) return `${m / 1440} day${m / 1440 > 1 ? "s" : ""}`;
  if (m % 60 === 0) return `${m / 60} hr`;
  return `${m} min`;
}

export function DemoSettings() {
  const [dialog, setDialog] = useState<null | "billing" | "upgrade">(null);
  const seatsUsed = ROSTER.filter((r) => r.status === "active").length;

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <header className="mb-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">Settings</p>
        <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">Account &amp; settings</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
          Your profile, notifications, and agreements. This is a static sample — nothing here is saved.
        </p>
      </header>

      {/* ── Profile: clinic identity ─────────────────────────────────────────── */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-[var(--color-border-gold)] text-[var(--color-gold-soft)]">
              <Building2 className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading text-[18px] font-medium text-[var(--color-text-primary)]">{DEMO_THERAPIST.clinic}</p>
              <p className="text-[12px] text-[var(--color-text-ghost)]">Group plan · {seatsUsed} of 5 seats in use</p>
            </div>
          </div>
          <span className="rounded-full border border-[var(--color-border-gold)] bg-[var(--color-navy-light)] px-3 py-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)]">BAA on file</span>
        </div>
      </section>

      {/* ── Profile: therapist roster ────────────────────────────────────────── */}
      <section className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Therapists</h2>
        </div>
        <ul className="mt-3 space-y-2">
          {ROSTER.map((r) => (
            <li key={r.name} className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] font-mono text-[11px] text-[var(--color-text-muted)]">{r.initials}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-[14px] font-medium text-[var(--color-text-primary)]">{r.name}</p>
                <p className="text-[11px] text-[var(--color-text-ghost)]">{r.role}</p>
              </div>
              <span className={cn(
                "rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wide",
                r.status === "active" ? "bg-[#1a3020] text-[#6bbd8a]" : "border border-[var(--color-border-gold)] text-[var(--color-gold-soft)]",
              )}>
                {r.status === "active" ? "Active" : "Invited"}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Profile: billing ─────────────────────────────────────────────────── */}
      <section className="mt-4 flex flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Billing</h2>
        </div>
        <p className="mt-2 text-[13px] text-[var(--color-text-muted)]">Group plan · graduated per-seat. Next invoice in 18 days.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => setDialog("billing")} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border-strong)] px-3.5 py-2 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)]">Manage billing</button>
          <button type="button" onClick={() => setDialog("upgrade")} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-3.5 py-2 font-mono text-[10px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]">Upgrade plan</button>
        </div>
      </section>

      {/* ── Account & notifications ──────────────────────────────────────────── */}
      <section className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Account &amp; notifications</h2>
        </div>

        <dl className="mt-3 divide-y divide-[var(--color-border)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)]">
          <div className="flex items-center gap-3 px-4 py-3">
            <Mail className="h-4 w-4 shrink-0 text-[var(--color-text-ghost)]" />
            <dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)]">Email</dt>
            <dd className="min-w-0 flex-1 truncate text-[14px] text-[var(--color-text-primary)]">{DEMO_THERAPIST.email}</dd>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <Clock className="h-4 w-4 shrink-0 text-[var(--color-text-ghost)]" />
            <dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)]">Timezone</dt>
            <dd className="min-w-0 flex-1 truncate text-[14px] text-[var(--color-text-primary)]">{DEMO_THERAPIST.timezone}</dd>
          </div>
        </dl>

        {/* Reminder channels */}
        <div className="mt-4 flex items-center gap-2">
          <Bell className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h3 className="font-heading text-[14px] font-medium text-[var(--color-text-primary)]">Reminder channels</h3>
        </div>
        <ul className="mt-2 space-y-2">
          {NOTIFICATION_PREFS.map((pref) => (
            <li key={pref.id} className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-[var(--color-text-primary)]">{pref.label}</p>
                <p className="text-[11px] text-[var(--color-text-ghost)]">{pref.hint}</p>
              </div>
              <DemoToggle label={pref.label} defaultOn={pref.on} />
            </li>
          ))}
        </ul>

        {/* Lead times */}
        <h3 className="mt-4 font-heading text-[14px] font-medium text-[var(--color-text-primary)]">When reminders go out</h3>
        <p className="mt-1 text-[11px] text-[var(--color-text-ghost)]">How far ahead of each appointment to send reminders.</p>
        <div className="mt-2 space-y-3">
          <LeadChips label="Remind the client" defaultSelected={[1440]} />
          <LeadChips label="Remind me (therapist)" defaultSelected={[15]} />
        </div>

        <p className="mt-3 text-[11px] text-[var(--color-text-ghost)]">Saved automatically in the live app · disabled in this demo.</p>
      </section>

      {/* ── Agreements & documents ───────────────────────────────────────────── */}
      <section className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Agreements &amp; documents</h2>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
          At sign-up you sign a HIPAA Business Associate Agreement and a compliance acknowledgment — even on the free
          tier. These are the real Psy180 templates, shown here as read-only samples.
        </p>
        <div className="mt-4 space-y-3">
          <SampleConsentDoc formType="platform_baa" states={["WA", "UT"]} clinicName={DEMO_THERAPIST.clinic} defaultOpen />
          <SampleConsentDoc formType="hipaa_acknowledgment" states={["WA", "UT"]} clinicName={DEMO_THERAPIST.clinic} />
          <SampleConsentDoc formType="npp" states={["HIPAA"]} clinicName={DEMO_THERAPIST.clinic} />
        </div>
      </section>

      {dialog && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" style={{ background: "rgba(8,12,20,0.8)", backdropFilter: "blur(3px)" }} onClick={() => setDialog(null)}>
          <div className="w-full max-w-md rounded-2xl border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-heading text-xl font-medium text-[var(--color-text-primary)]">{dialog === "billing" ? "Manage billing" : "Upgrade plan"}</h3>
              <button type="button" onClick={() => setDialog(null)} aria-label="Close" className="rounded-md border border-[var(--color-border)] p-1.5 text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-3 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
              In the live app this opens your secure billing portal to {dialog === "billing" ? "update your card, see invoices, or change seats" : "move to a higher plan and add seats"}. It&apos;s disabled in this demo — no real payment details are ever handled here.
            </p>
            <button type="button" onClick={() => setDialog(null)} className="mt-5 w-full rounded-lg border border-[var(--color-border-gold)] py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-light)]">Got it</button>
          </div>
        </div>,
        document.body,
      )}
    </main>
  );
}

/** A small inert switch — flips local state for feel, but persists nothing. */
function DemoToggle({ label, defaultOn }: { label: string; defaultOn: boolean }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => setOn((v) => !v)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors",
        on ? "border-[var(--color-border-gold)] bg-[var(--color-gold)]" : "border-[var(--color-border)] bg-[var(--color-navy-light)]",
      )}
    >
      <span className={cn("inline-block h-4 w-4 transform rounded-full transition-transform", on ? "translate-x-5 bg-[var(--color-bg-deep)]" : "translate-x-1 bg-[var(--color-text-ghost)]")} />
    </button>
  );
}

/** Inert lead-time chip group — clicking toggles local state, reset on reload. */
function LeadChips({ label, defaultSelected }: { label: string; defaultSelected: number[] }) {
  const [selected, setSelected] = useState<number[]>(defaultSelected);
  const chips = Array.from(new Set([...LEAD_PRESETS, ...defaultSelected])).sort((a, b) => a - b);
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] block mb-2">{label}</span>
      <div className="flex flex-wrap gap-2">
        {chips.map((m) => {
          const active = selected.includes(m);
          return (
            <button
              key={m}
              type="button"
              aria-pressed={active}
              onClick={() => setSelected((s) => (s.includes(m) ? s.filter((x) => x !== m) : [...s, m].sort((a, b) => a - b)))}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs transition-colors",
                active ? "border-[var(--color-gold)] bg-[var(--color-gold)] text-[var(--color-bg-deep)]" : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-gold-soft)]",
              )}
            >
              {formatMinutes(m)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
